/**
 * Resolve a product/site image URL to a fully-qualified absolute URL, optionally
 * asking the backend for a WebP-optimized, right-sized variant.
 *
 *   resolveImage('/api/files/products/beef-brisket.png')          // original
 *   resolveImage('/api/files/products/beef-brisket.png', 320)     // ?w=320&fmt=webp
 *   resolveImage('/hero/trade-dining-1024.webp')                  // local public asset
 *   resolveImage('https://cdn.example.com/foo.jpg')               // absolute URL, untouched
 *
 * The backend accepts widths in {160, 240, 320, 480, 640, 960, 1280} — passing
 * an unlisted width will fail with 400. This helper snaps to the nearest
 * supported width so callers can pass their intended display size directly.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const ALLOWED_WIDTHS = [160, 240, 320, 480, 640, 960, 1280];

function snapWidth(w) {
  if (!w || typeof w !== 'number') return null;
  // Pick the smallest allowed width ≥ requested (or the max if larger than 1280).
  for (const aw of ALLOWED_WIDTHS) if (aw >= w) return aw;
  return ALLOWED_WIDTHS[ALLOWED_WIDTHS.length - 1];
}

export function resolveImage(src, width) {
  if (!src) return '';
  // Absolute URL — leave alone (external CDN, already-full backend URL, etc).
  if (/^https?:\/\//i.test(src)) return src;
  // Backend-relative — prefix with REACT_APP_BACKEND_URL.
  if (src.startsWith('/api/')) {
    const w = snapWidth(width);
    // Only add the `w` param for stored files (products / hero); other
    // /api routes like /api/catalogue.pdf must not be image-optimized.
    if (w && /\/files\//.test(src)) {
      const sep = src.includes('?') ? '&' : '?';
      return `${BACKEND}${src}${sep}w=${w}&fmt=webp`;
    }
    return `${BACKEND}${src}`;
  }
  // Local public asset (e.g. /hero/trade-dining-640.webp)
  return src;
}

export default resolveImage;
